// client/src/utils/CivilEngineeringData.js

export const CivilEngineeringData = {
  // --- 1. VASTU SHASTRA RULES ---
  vastu: {
    north: {
      tips: [
        "Entrance: North is ideal for the main entrance (Kuber Sthan).",
        "Living Room: Place in the North or East for positive energy.",
        "Avoid: Septic tanks or toilets in the North-East corner (Ishan Kon).",
        "Colors: Use light shades like white, off-white, or light blue.",
      ],
      reasoning: "North is associated with Lord Kuber (Wealth) and is a highly positive direction.",
    },
    south: {
      tips: [
        "Bedroom: Master bedroom in South-West is stable and auspicious.",
        "Heavy Structures: Keep South and West heavier and higher than North/East.",
        "Avoid: Main entrance in exact South (Yama Sthan) if possible, or use a specific pada.",
      ],
      reasoning: "South stores energy; South-West provides stability for the head of the family.",
    },
    east: {
      tips: [
        "Entrance: East facing entrance is very auspicious (Indra Sthan).",
        "Study Room: Facing East while studying improves concentration.",
        "Open Space: Leave more open space in the East.",
      ],
      reasoning: "East brings the energy of the rising sun (health and clarity).",
    },
    west: {
      tips: [
        "Dining: West is good for the dining area.",
        "Kids Room: West or North-West is suitable for children.",
        "Overhead Tank: Best placed in the West or South-West.",
      ],
      reasoning: "West is ruled by Varun (Water) and is good for stability and gains.",
    },
    kitchen: {
      best: "South-East (Agneya Kon)",
      alternative: "North-West (Vayuvya Kon)",
      avoid: "North-East (Ishan Kon)",
      tip: "Cook facing East for health.",
    },
    bedroom: {
      master: "South-West (Stability)",
      kids: "North-West (Movement/Growth)",
      guest: "North-West",
    },
    pooja: {
      best: "North-East (Ishan Kon)",
      tip: "Face East or North while praying.",
    },
  },

  // --- 2. MATERIAL PROPERTIES & COMPARISONS ---
  materials: {
    cement: {
      opc: {
        name: "Ordinary Portland Cement (OPC)",
        grades: ["33", "43", "53"],
        uses: "Structural concrete, RCC (columns, beams, slabs), heavy load bearing.",
        pros: "High initial strength, faster setting.",
        cons: "Higher heat of hydration (can cause cracks if not cured well), less durable in aggressive environments.",
      },
      ppc: {
        name: "Portland Pozjolana Cement (PPC)",
        uses: "Plastering, masonry, brickwork, waterproofing, mass concreting (dams).",
        pros: "Better workability, lower heat of hydration (less cracks), chemical resistance, cheaper.",
        cons: "Slower setting time, lower initial strength (but equal final strength).",
      },
      white: {
        name: "White Cement",
        uses: "Decorative work, flooring, terrazzo, putty.",
      },
    },
    steel: {
      tmt: {
        name: "TMT Bars (Thermo Mechanically Treated)",
        grades: ["Fe415", "Fe500", "Fe500D", "Fe550"],
        uses: "RCC reinforcement.",
        pros: "High tensile strength, ductility, earthquake resistance.",
      },
    },
    sand: {
      river: {
        name: "River Sand",
        uses: "Plastering, concreting (if clean).",
        pros: "Natural smooth texture.",
        cons: "Ecological damage, often contains silt/clay.",
      },
      msand: {
        name: "M-Sand (Manufactured Sand)",
        uses: "Concrete, brickwork.",
        pros: "Eco-friendly, stronger concrete bond, cheaper.",
        cons: "Rough texture (needs good washing/grading).",
      },
    },
  },

  // --- 3. ESTIMATION FORMULAS ---
  formulas: {
    steel_footing: {
      description: "Steel estimation for footing",
      rule: "0.8% to 2% of concrete volume (Use 0.8% for residential)",
      density: 7850, // kg/m3
      calc: (vol) => vol * 0.008 * 7850,
    },
    steel_column: {
      description: "Steel estimation for column",
      rule: "0.8% to 6% of gross cross-sectional area (Use 1.5% avg)",
      density: 7850,
      calc: (vol) => vol * 0.015 * 7850,
    },
    steel_beam: {
      description: "Steel estimation for beam",
      rule: "1% to 2% of concrete volume (Use 1.2% avg)",
      density: 7850,
      calc: (vol) => vol * 0.012 * 7850,
    },
    steel_slab: {
      description: "Steel estimation for slab",
      rule: "0.7% to 1% of concrete volume (Use 0.8% avg)",
      density: 7850,
      calc: (vol) => vol * 0.008 * 7850,
    },
    concrete_mix: {
      m20: { ratio: "1:1.5:3", cement_bags_per_m3: 8 },
      m25: { ratio: "1:1:2", cement_bags_per_m3: 11 },
    },
    bricks: {
      standard: {
        size_mm: "190x90x90",
        with_mortar: "200x100x100",
        calc: (vol_m3) => Math.ceil(vol_m3 / (0.2 * 0.1 * 0.1)), // approx 500 bricks/m3
      },
    },
  },

  // --- 4. GENERAL Q&A ---
  general: {
    "alkaline": "For alkaline environments or soils with high sulphate, use **Sulphate Resisting Portland Cement (SRPC)** or **PPC** to prevent chemical attack.",
    "curing": "Curing should be done for at least **7-14 days**. It ensures concrete attains full strength and prevents cracking.",
    "cover": "Clear cover: Footing (50mm), Column (40mm), Beam (25mm), Slab (20mm).",
    "lapping": "Lapping zone for columns is at the mid-height. Avoid lapping at joints.",
    "rebar": "Rebar (Reinforcing Bar) is a steel bar or mesh of steel wires used as a tension device in reinforced concrete and reinforced masonry structures to strengthen and aid the concrete under tension."
  },

  // --- 5. CONCRETE TECHNOLOGY ---
  concrete: {
    slump: [
      { range: "0-25 mm", type: "Very Low", use: "Roads, Pavements (Vibrated)" },
      { range: "25-50 mm", type: "Low", use: "Mass Concrete, Lightly Reinforced Sections" },
      { range: "50-100 mm", type: "Medium", use: "Normal RCC (Beams, Columns, Slabs), Pumpable Concrete" },
      { range: "100-150 mm", type: "High", use: "Congested Reinforcement, Deep Sections, Piling" },
      { range: ">150 mm", type: "Very High", use: "Self Compacting Concrete (SCC)" }
    ],
    mix_design: {
      steps: [
        "1. Target Mean Strength",
        "2. Selection of W/C Ratio",
        "3. Selection of Water Content (based on Slump)",
        "4. Calculation of Cement Content",
        "5. Estimation of Aggregate Proportions"
      ],
      water_content: {
        "25-50": 186, // Liters per m3 for 20mm agg
        "50-100": 200, // Approx increase
        "100-150": 210
      }
    }
  }
};
